/**
 * Rock Detail Dialog
 *
 * Modal overlay that displays detailed rock information.
 * Consumes the RockDetailContext for open/close state and back stack navigation,
 * and derives the displayed rock from the React Query rocks cache by id-lookup.
 *
 * Because the cache is the source of truth, any ["rocks"] invalidation (after
 * a weekly health check-in, a lifecycle transition, or an inline edit) flows
 * through to the modal automatically — no manual updateCurrentRock needed.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { headerClasses } from "@/lib/ui-theme";
import { useRockDetail } from "./rock-detail-context";
import { useRocks } from "@/contexts/rocks-context";
import { ShortCodeBadge } from "./short-code-badge";
import { RockDetailContent } from "./rock-detail-view";
import { formatRelativeTime } from "@/lib/utils";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { VisuallyHidden } from "radix-ui";
import { useEffect, useMemo } from "react";
import { getRockById } from "@/lib/load-goals";
import { useProfileData } from "@/hooks/use-profile-data";
import { useCapabilities } from "@/hooks/use-capabilities";

export function RockDetailDialog() {
  const {
    currentRockId,
    pendingNewRock,
    clearPendingNewRock,
    isOpen,
    closeRock,
    canGoBack,
    goBack,
    isReadOnly,
  } = useRockDetail();
  const { data } = useRocks();
  const { data: profileData } = useProfileData();
  const { can } = useCapabilities();

  // Derive the displayed rock from the rocks cache by id-lookup. The cache
  // is the single source of truth — weekly check-ins, lifecycle transitions,
  // and inline edits all refresh through here automatically.
  const rockFromCache = useMemo(() => {
    if (!currentRockId || !data) return null;
    return getRockById(data, currentRockId);
  }, [currentRockId, data]);

  // Fallback for the create-flow race: a newly created rock may not be in
  // the cache yet when openRock() fires. Use pendingNewRock until the
  // cache refetch lands, then clear it.
  const currentRock = rockFromCache ?? (
    pendingNewRock && pendingNewRock.id === currentRockId ? pendingNewRock : null
  );

  // Clear pendingNewRock once the cache catches up — keeps the fallback
  // strictly transient.
  useEffect(() => {
    if (pendingNewRock && rockFromCache && rockFromCache.id === pendingNewRock.id) {
      clearPendingNewRock();
    }
  }, [pendingNewRock, rockFromCache, clearPendingNewRock]);

  // Deleted-while-open: the modal is open with a rock id that no longer
  // exists in the cache (deleted by this user or another) and there is no
  // pending new-rock fallback for the same id. Close cleanly rather than
  // rendering null and leaving the Dialog stuck.
  useEffect(() => {
    const hasFallback = pendingNewRock?.id === currentRockId;
    if (isOpen && currentRockId && data && !rockFromCache && !hasFallback) {
      closeRock();
    }
  }, [isOpen, currentRockId, data, rockFromCache, pendingNewRock, closeRock]);

  // Count child rocks that will be orphaned if this rock is deleted
  // Note: Must be called before early return to maintain hooks order
  const childRockCount = useMemo(() => {
    if (!currentRock || !data) return 0;
    const rocks = [...data.leadership_rocks, ...(data.cascaded_rocks ?? [])];
    return rocks.filter((r) => r.linked_to_parent_rock === currentRock.id).length;
  }, [currentRock, data]);

  if (!currentRock || !data) return null;

  // Permission check for delete button
  // Admin: can delete any rock
  // Manager: can delete draft rocks
  // Owner: can only delete draft rocks they own
  // TODO: Remove owner_user_id fallback when user-tenancy work links all users to profiles
  const isDraft = currentRock.status === "draft";
  const isOwner = !!(
    profileData?.profile_id && currentRock.profile_id === profileData.profile_id
  ) || !!(profileData?.id && currentRock.owner_user_id === profileData.id);
  const canDelete = can("rocks.manage") || (isDraft && isOwner);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeRock(); }}>
      <DialogContent className="max-w-[95vw] md:max-w-5xl max-h-[90vh] overflow-hidden p-0 gap-0 flex flex-col [&>button:last-child]:hidden">
        <VisuallyHidden.Root>
          <DialogTitle>{currentRock.title}</DialogTitle>
          <DialogDescription>Rock detail view for {currentRock.short_code}</DialogDescription>
        </VisuallyHidden.Root>

        {/* Header - fixed at top, not scrollable */}
        <div
          className={cn(
            "flex items-center justify-between gap-3 px-4 py-2.5 border-b shrink-0",
            headerClasses.primary
          )}
        >
          <div className="flex items-center gap-2 min-w-0">
            {canGoBack && (
              <button
                onClick={goBack}
                className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors -my-1 shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
            <ShortCodeBadge shortCode={currentRock.short_code} className="text-[10px] bg-white/10 border-white/30 text-white" />
            <span className="text-sm font-medium text-white truncate">{currentRock.title}</span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {currentRock._updated_at && (
              <div className="text-[10px] text-white/70 flex items-center gap-1.5">
                <span>Updated {formatRelativeTime(currentRock._updated_at)}</span>
                {currentRock._updated_by && currentRock._updated_by !== "migration" && (
                  <span className="text-white/50">by {currentRock._updated_by}</span>
                )}
              </div>
            )}
            <button
              onClick={closeRock}
              className="flex items-center justify-center h-7 w-7 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors -my-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Scrollable content area - scrollbar only appears here, below the header */}
        <div className="overflow-y-auto flex-1 min-h-0" style={{ willChange: "scroll-position" }}>
          <ErrorBoundary label="rock details">
            <RockDetailContent
              key={currentRock.id}
              rock={currentRock}
              data={data}
              canDelete={canDelete}
              childRockCount={childRockCount}
              onDeleted={closeRock}
              readOnly={isReadOnly}
            />
          </ErrorBoundary>
        </div>
      </DialogContent>
    </Dialog>
  );
}
